import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Save, Building } from "lucide-react";
import TimezoneSelector from "./TimezoneSelector";

export default function PracticeInfoForm() {
  const queryClient = useQueryClient();

  // Fetch practice data using React Query
  const { data: practice, isLoading } = useQuery({
    queryKey: ["practice"],
    queryFn: () => apiClient.get(ENDPOINTS.SETTINGS.PRACTICE),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    registrationNumber: "",
    taxId: "",
    timezone: "Africa/Johannesburg", // Default timezone
  });

  // Update form data when practice data is loaded
  useEffect(() => {
    if (practice) {
      setFormData({
        name: practice.name || "",
        address: practice.address || "",
        phone: practice.phone || "",
        email: practice.email || "",
        website: practice.website || "",
        registrationNumber: practice.registrationNumber || "",
        taxId: practice.taxId || "",
        timezone: practice.timezone || "Africa/Johannesburg", // Default to South Africa
      });
    }
  }, [practice]);

  // Mutation for updating practice
  const updatePracticeMutation = useMutation({
    mutationFn: (practiceData) => apiClient.put(ENDPOINTS.SETTINGS.PRACTICE, practiceData),
    onSuccess: () => {
      queryClient.invalidateQueries(["practice"]);
    },
  });

  const handleSave = async (e) => {
    e.preventDefault();
    updatePracticeMutation.mutate(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (isLoading) {
    return (
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="w-5 h-5" />
            Practice Information
          </CardTitle>
          <CardDescription>
            Loading practice information...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="animate-pulse bg-slate-200 h-10 rounded"></div>
            <div className="animate-pulse bg-slate-200 h-10 rounded"></div>
            <div className="animate-pulse bg-slate-200 h-10 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building className="w-5 h-5" />
          Practice Information
        </CardTitle>
        <CardDescription>
          Update your practice&apos;s information and contact details.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Practice Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Club Med Health Center"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="e.g., +27-21-555-0100"
                required
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="e.g., 123 Wellness Avenue, Cape Town, South Africa"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="e.g., info@clubmedhealth.co.za"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="registrationNumber">Registration Number</Label>
              <Input
                id="registrationNumber"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleChange}
                placeholder="Business registration number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="taxId">Tax ID</Label>
              <Input
                id="taxId"
                name="taxId"
                value={formData.taxId}
                onChange={handleChange}
                placeholder="Tax identification number"
              />
            </div>
          </div>
          
          {/* Timezone Selector - Full Width */}
          <div className="mt-6">
            <TimezoneSelector
              value={formData.timezone}
              onChange={(timezone) => setFormData(prev => ({...prev, timezone}))}
              disabled={updatePracticeMutation.isPending}
              showCurrentTime={true}
            />
          </div>
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={updatePracticeMutation.isPending}
            >
              <Save className="w-4 h-4 mr-2" />
              {updatePracticeMutation.isPending ? "Saving..." : "Save Practice Info"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
